class FreeShippingGoal extends HTMLElement {
  #cartChangeHandler = null;

  connectedCallback() {
    this.#cartChangeHandler = this.#onCartChange.bind(this);
    document.addEventListener("cart:change", this.#cartChangeHandler);
    this.#updateFromDataset();
  }

  disconnectedCallback() {
    if (this.#cartChangeHandler) {
      document.removeEventListener("cart:change", this.#cartChangeHandler);
      this.#cartChangeHandler = null;
    }
  }

  #onCartChange(event) {
    const cart = event?.detail?.cart;
    if (!cart) return;

    if (cart.item_count === 0) {
      this.hidden = true;
      return;
    }

    if (typeof cart.total_price !== "number") return;
    this.#update(cart.total_price);
  }

  #updateFromDataset() {
    const cartTotal = this.#parsePositiveInt(this.dataset.cartTotal, 0);
    this.#update(cartTotal);
  }

  #update(cartTotalCents) {
    const baseThreshold = this.#parsePositiveInt(this.dataset.threshold, 0);
    if (!baseThreshold) {
      this.hidden = true;
      return;
    }

    const threshold = this.#getThresholdCents(baseThreshold);
    const safeCartTotal = Math.max(0, this.#parsePositiveInt(cartTotalCents, 0));
    const reached = safeCartTotal >= threshold;
    const remaining = Math.max(0, threshold - safeCartTotal);
    const progress = this.#clampProgress((safeCartTotal / threshold) * 100);

    const messageEl = this.querySelector("[data-message]");
    const progressBar = this.querySelector("[data-progressbar]");
    const progressFill = this.querySelector("[data-progress-fill]");

    if (messageEl) {
      messageEl.textContent = reached
        ? this.dataset.successMessage || ""
        : this.#formatProgressMessage(remaining);
    }

    if (progressFill) {
      progressFill.style.setProperty("--free-shipping-progress", `${progress}%`);
    }

    if (progressBar) {
      progressBar.setAttribute("aria-valuenow", String(Math.round(progress)));
      progressBar.setAttribute(
        "aria-label",
        this.#getProgressLabel(reached, progress)
      );
    }

    this.toggleAttribute("data-reached", reached);
    this.hidden = false;
  }

  #formatProgressMessage(remainingCents) {
    const template = this.dataset.progressMessage || "";
    const formattedAmount = this.#formatMoney(remainingCents);
    return template.replace(/\[amount\]/g, formattedAmount);
  }

  #getProgressLabel(reached, progress) {
    const baseLabel =
      window.freeShippingGoalStrings?.progressLabel || "Free shipping progress";

    if (reached) {
      return `${baseLabel}: 100%`;
    }

    return `${baseLabel}: ${Math.round(progress)}%`;
  }

  #getThresholdCents(baseThresholdCents) {
    const rate = this.#getCurrencyRate();
    return Math.round(baseThresholdCents * rate);
  }

  #getCurrencyRate() {
    const rate = parseFloat(window.Shopify?.currency?.rate);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  }

  #clampProgress(value) {
    if (!Number.isFinite(value)) return 0;
    return Math.min(100, Math.max(0, value));
  }

  #parsePositiveInt(value, fallback = 0) {
    const parsed = parseInt(value, 10);
    if (!Number.isFinite(parsed) || parsed < 0) return fallback;
    return parsed;
  }

  #formatMoney(cents) {
    const format =
      this.dataset.moneyFormat ||
      window.theme?.moneyFormat ||
      "${{amount}}";

    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholderRegex);
    if (!match) return String(cents / 100);

    let formattedValue = "";

    switch (match[1]) {
      case "amount":
        formattedValue = this.#formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        formattedValue = this.#formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        formattedValue = this.#formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        formattedValue = this.#formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        formattedValue = this.#formatWithDelimiters(cents, 2, "'", ".");
        break;
      default:
        formattedValue = this.#formatWithDelimiters(cents, 2);
    }

    return format.replace(placeholderRegex, formattedValue);
  }

  #formatWithDelimiters(cents, precision = 2, thousands = ",", decimal = ".") {
    if (!Number.isFinite(cents)) return "0";

    const value = (cents / 100).toFixed(precision);
    const parts = value.split(".");
    const dollars = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands}`
    );
    const centsPart = parts[1] ? `${decimal}${parts[1]}` : "";

    return dollars + centsPart;
  }
}

if (!customElements.get("free-shipping-goal")) {
  customElements.define("free-shipping-goal", FreeShippingGoal);
}
